import { any, array, string, number } from 'prop-types';
import { useState, useEffect } from 'react';
import { Row, Col } from 'antd';

import { useLocale } from 'hooks';
import { checkProfile, getSingleTranslation } from 'actions/general';
import { searchProductsUnbxd, searchByImageUnbxd, retrieveImageForSearch } from 'actions/search';
import Layout from 'components/layout';
import { HeadSEO } from 'components/base';
import PaginatedGrid from 'components/paginatedGrid';
import { sorts } from 'utils/filters';
import FilterPanelSearcher from 'components/search/filterPanel';
import ProductPlaceholder from 'components/productPlaceholder';
import { initCMS } from 'utils/cms';

import { useI18n } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { searchProductsGTM, parseProductsEcommerceGA4 } from 'utils/gtmUtils';
import { getMarket, getRealLanguage, getLang, DEFAULT_LIMIT, COOKIES, getCookie, getProductsUnbxdFormatted } from 'utils/helpers';
import { getIfBlank, isBlank } from 'utils/StringUtils';
import { useGridVouchers } from 'hooks/useGridVouchers';
import { FilterHeader } from 'components/filter';
import { getDomainValue } from 'utils/domain';

import styles from 'styles/Search.module.css';

const minSearchLength = 1;

const DEFAULT_SORT = Object.keys(sorts)[0];

function Search({
  defaultProps,
  products: serverProducts = null,
  searchTerm = '',
  totalProducts = 0,
  facets,
  filteredFacets = {},
  unbxdRequestId = '',
  unbxdTranslations,
  fallback = null,
  queryMode,
  imageUrl,
}) {
  const { t, searchQuery, profileData } = useI18n();
  const { language, locale, country } = useLocale();
  const isoLanguage = getRealLanguage(language);

  const targetsTranslations = {
    W: t('generico', 'target.mujer', 'Women'),
    M: t('generico', 'target.hombre', 'Men'),
    K: t('generico', 'target.kids', 'Kids'),
    G: t('generico', 'target.goods', 'Goods'),
    X: t('generico', 'target.unisex', 'Unisex'),
    A: t('buscador', 'prefiltro.todos', 'See all'),
  };
  const [products] = useState(() => serverProducts);
  const [loading, setLoading] = useState(false);
  const [emptyResults, setEmptyResults] = useState(false);
  const [lastTerm, setLastTerm] = useState(searchTerm);
  const [filterPanelVisible, setFilterPanelVisible] = useState(false);
  const [currentFilterQuery, setCurrentFilterQuery] = useState('');
  const [productsPerPage, setProductsPerPage] = useState({
    1: serverProducts,
  });
  const [currentTotal, setCurrentTotal] = useState(totalProducts);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentSort, setCurrentSort] = useState(DEFAULT_SORT);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [currentFacets, setCurrentFacets] = useState(JSON.parse(JSON.stringify(Object.keys(filteredFacets).length ? filteredFacets : facets)));
  const [useCurrentFacets, setUseCurrentFacets] = useState(false);
  const [fallbackSearch, setFallbackSearch] = useState(fallback);

  const originalCta = {};

  useGridVouchers({ firstProducts: serverProducts, originalCta });

  // filteredProducts should also be derivedState. We filter the products based on the activeTarget, or show all. This will update when either products or activeTarget changes

  const listName = `${t('buscador', 'results.for', 'Results for')} ${queryMode !== '' ? t('search', 'your.search') : `"${fallbackSearch?.query || searchQuery || searchTerm}"`}`;

  useEffect(() => {
    if (!isBlank(searchTerm) && searchTerm.length >= minSearchLength) {
      const emptyProducts = !products || products.length === 0;
      const productSearchEvent = {
        event: 'productSearch',
        items: searchProductsGTM(products || []),
      };
      const viewSearchResultsEvent = {
        event: 'view_search_results',
        search_term: searchTerm,
        num_results: emptyProducts ? '0' : String(products.length),
      };
      productSearchEvent.unbxd_request_id = unbxdRequestId;
      viewSearchResultsEvent.unbxd_request_id = unbxdRequestId;
      sessionStorage.setItem('unbxdRequestId', unbxdRequestId);
      dataLayerHandleEvent(productSearchEvent);
      dataLayerHandleEvent(viewSearchResultsEvent);
      dataLayerHandleEvent({
        event: 'view_item_list',
        ecommerce: {
          currency: profileData?.currencySap || (products?.length ? products?.[0]?.prices?.currency : ''),
          items: emptyProducts ? [] : parseProductsEcommerceGA4(products, 'view_search_results', listName),
        },
      });
      setEmptyResults(emptyProducts);
    } else if (queryMode !== '') {
      const emptyProducts = !products || products.length === 0;
      setEmptyResults(emptyProducts);
    }
    const availableFilters = [];
    if (currentFacets?.text?.list && currentFacets?.text?.list?.length) {
      currentFacets?.text?.list.map((filter) => {
        if (filter.values) {
          filter.values
            .filter((element, index) => index % 2 === 0)
            .map(() => {
              availableFilters.push(filter.filterField);
              return null;
            });
        }
        return null;
      });
      sessionStorage.setItem('unbxdRequestId', unbxdRequestId);
    }
    setShouldFetch(true);
  }, []);

  const handleSearchProductsUnbxd = (searchValue, page, sort, filter, callback) => {
    const unbxdUid = getCookie(COOKIES.UNBXD_UID) || null;
    if (queryMode === 'findSimilar' && imageUrl) {
      searchByImageUnbxd({
        image: imageUrl,
        lang: isoLanguage,
        market: country,
        page,
        sort,
        filter,
        uid: unbxdUid,
        translations: unbxdTranslations,
      }).then(callback);
    } else {
      searchProductsUnbxd({
        query: searchValue,
        lang: isoLanguage,
        market: country,
        page,
        sort,
        filter,
        uid: unbxdUid,
        translations: unbxdTranslations,
      }).then((response) => callback(response));
    }
  };

  const handleOnClickFilterBtn = (visible, fakeApply = false) => {
    document.querySelector('body').classList.toggle('filterOpen');
    setFilterPanelVisible(visible);
    if (fakeApply) {
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleApplyFilters = async (filters, sort = DEFAULT_SORT) => {
    let filtersQuery = '';
    if (filters.length) {
      filters.map((filter) => {
        const filterType = filter.split(':').shift();
        const filterValue = filter.split(':').pop();
        if (!filtersQuery.includes(filterType)) {
          filtersQuery = `${filtersQuery}&filter=${filterType}:"${filterValue}"`;
        } else {
          const filterQuerySplitted = filtersQuery.split(`${filterType}:`);
          if (filterQuerySplitted.length < 2) {
            filtersQuery = `${filterQuerySplitted[0]}${filterType}:"${filterValue}" OR ${filterType}:"${filterQuerySplitted[1]}"`;
          } else {
            filtersQuery = `${filtersQuery} OR ${filterType}:"${filterValue}"`;
          }
        }
        return null;
      });
    }
    // ñapa porque si aplican un filtro target en realidad a la api le tenemos que enviar el filtro de gender
    const targetsFilter = ['en_target_uFilter:Men', 'en_target_uFilter:Women', 'en_target_uFilter:Unisex'];
    targetsFilter.map((targetFilter) => {
      if (filtersQuery.includes(targetFilter)) {
        const genderFilter = targetFilter.replace('_target_', '_gender_');
        filtersQuery = filtersQuery.split(targetFilter).join(genderFilter);
      }
      return null;
    });
    setShouldFetch(false);
    setAppliedFilters(filters.length ? filters : []);
    setCurrentFilterQuery(filtersQuery);
    setCurrentSort(sort);
    setLoading(true);
    handleSearchProductsUnbxd(lastTerm, 1, sort, filtersQuery, (response) => {
      const productsFormatted = getProductsUnbxdFormatted(response.products, locale) ?? [];
      const [body] = document.getElementsByTagName('body');
      if (body) {
        body.scrollIntoView();
      }
      setProductsPerPage({
        1: productsFormatted,
      });
      setLoading(false);
      setShouldFetch(true);
      setCurrentTotal(response.numberOfProducts);
      setCurrentFacets(response.facets || {});
      if (response?.unbxdRequestId) {
        sessionStorage.setItem('unbxdRequestId', response.unbxdRequestId);
      }
      dataLayerHandleEvent({
        event: 'view_item_list',
        ecommerce: {
          currency: profileData?.currencySap || (productsFormatted?.length ? productsFormatted?.[0]?.prices?.currency : ''),
          items: productsFormatted.length ? parseProductsEcommerceGA4(productsFormatted, 'view_search_results', listName) : [],
        },
      });
    });
  };

  const handleLoadNextPage = (nextPageNumber) => {
    const currentLoadedPages = Object.keys(productsPerPage);
    if (shouldFetch && nextPageNumber >= 1 && !currentLoadedPages.includes(String(nextPageNumber))) {
      const nextPageOffset = nextPageNumber > 1 ? (nextPageNumber - 1) * DEFAULT_LIMIT : 0;
      if (nextPageOffset < currentTotal) {
        handleSearchProductsUnbxd(lastTerm, nextPageNumber, currentSort, currentFilterQuery, (response) => {
          const newProductsPerPage = JSON.parse(JSON.stringify(productsPerPage));
          const productsFormatted = getProductsUnbxdFormatted(response.products, locale) ?? [];
          newProductsPerPage[nextPageNumber] = productsFormatted;
          setProductsPerPage(newProductsPerPage);
          setCurrentTotal(response.numberOfProducts);
          if (response?.unbxdRequestId) {
            sessionStorage.setItem('unbxdRequestId', response.unbxdRequestId);
          }
          dataLayerHandleEvent({
            event: 'view_item_list',
            ecommerce: {
              currency: profileData?.currencySap || (productsFormatted?.length ? productsFormatted?.[0]?.prices?.currency : ''),
              items: productsFormatted.length ? parseProductsEcommerceGA4(productsFormatted, 'view_search_results', listName) : [],
            },
          });
        });
      }
    }
  };

  useEffect(() => {
    if (shouldFetch) {
      setAppliedFilters([]);
      setCurrentFilterQuery('');
      setCurrentSort(DEFAULT_SORT);
      setLastTerm(searchQuery);
      setEmptyResults(false);
      setLoading(true);
      handleSearchProductsUnbxd(searchQuery, 1, DEFAULT_SORT, '', (response) => {
        setFallbackSearch(null);
        if (response?.redirect?.type === 'url' && !isBlank(response?.redirect?.value)) {
          window.location.assign(response?.redirect?.value);
          return;
        }
        const productsFormatted = getProductsUnbxdFormatted(response.products, locale) ?? [];
        const productSearchEvent = {
          event: 'productSearch',
          items: searchProductsGTM(products || []),
        };
        const viewSearchResultsEvent = {
          event: 'view_search_results',
          search_term: searchQuery,
          num_results: !productsFormatted.length ? '0' : String(productsFormatted.length),
        };
        productSearchEvent.unbxd_request_id = response?.unbxdRequestId || unbxdRequestId;
        viewSearchResultsEvent.unbxd_request_id = response?.unbxdRequestId || unbxdRequestId;
        if (response?.unbxdRequestId) {
          sessionStorage.setItem('unbxdRequestId', response.unbxdRequestId);
        }
        dataLayerHandleEvent(productSearchEvent);
        dataLayerHandleEvent(viewSearchResultsEvent);
        dataLayerHandleEvent({
          event: 'view_item_list',
          ecommerce: {
            currency: profileData?.currencySap || (products?.length ? products?.[0]?.prices?.currency : ''),
            items: !productsFormatted.length ? [] : parseProductsEcommerceGA4(productsFormatted, 'view_search_results', listName),
          },
        });
        if (response?.numberOfProducts === 1 && productsFormatted[0].url) {
          window.location.assign(productsFormatted[0].url);
        } else {
          const [body] = document.getElementsByTagName('body');
          if (body) {
            body.scrollIntoView();
          }
          setProductsPerPage({
            1: productsFormatted,
          });
          setLoading(false);
          setShouldFetch(true);
          setCurrentTotal(response.numberOfProducts);
          setEmptyResults(response.numberOfProducts === 0);
          setCurrentFacets(response.facets || {});
          setFallbackSearch(response.fallback);
          if (totalProducts === 0 && !useCurrentFacets && response.numberOfProducts > 0) {
            setUseCurrentFacets(true);
          }
          if (response.numberOfProducts > 0 && !useCurrentFacets) {
            setUseCurrentFacets(true);
          }
          const unbxdInput = document.querySelector('#unbxd-search-box');
          if (unbxdInput && unbxdInput.value !== searchQuery) {
            unbxdInput.value = searchQuery;
          }
        }
      });
    }
  }, [searchQuery]);

  // funciones de filtros
  const availableFiltersKeys = [];
  if (currentFacets?.text?.list && currentFacets?.text?.list?.length) {
    currentFacets?.text?.list.map((filter) => {
      if (filter.values) {
        filter.values
          .filter((element, index) => index % 2 === 0)
          .map((val) => {
            availableFiltersKeys.push(`${filter.filterField}:${val}`);
            return null;
          });
      }
      return null;
    });
  }

  const handleClickSort = (sortValue) => {
    dataLayerHandleEvent({
      event: 'eventGA',
      eventCat: 'ecommerce',
      eventAct: 'gridSort',
      eventLbl: sortValue,
    });
    const cumulatedFilters = {};
    appliedFilters.map((appliedFilter) => {
      const appliedFilterType = appliedFilter.split(':').shift().replace('_uFilter', '').replace('en_', '');
      const appliedFilterValue = appliedFilter.split(':').pop().toLowerCase();
      if (appliedFilterType in cumulatedFilters) {
        cumulatedFilters[appliedFilterType] = `${cumulatedFilters[appliedFilterType]}|${appliedFilterValue}`;
      } else {
        cumulatedFilters[appliedFilterType] = appliedFilterValue;
      }
      return null;
    });
    cumulatedFilters.sort_by = sortValue;
    dataLayerHandleEvent({
      event: 'filter_item',
      event_category: 'filters',
      event_detail_1: 'sort by',
      event_detail_2: sortValue,
      event_detail_3: 'add',
      cumulated_filters: cumulatedFilters,
    });
    handleApplyFilters(appliedFilters, sortValue);
  };

  const handleRemoveFilter = (filterValueToRemove) => {
    const currentFilters = [...appliedFilters].filter((filterValue) => filterValue !== filterValueToRemove);
    const cumulatedFilters = {};
    currentFilters.map((appliedFilter) => {
      const appliedFilterType = appliedFilter.split(':').shift().replace('_uFilter', '').replace('en_', '');
      const appliedFilterValue = appliedFilter.split(':').pop().toLowerCase();
      if (appliedFilterType in cumulatedFilters) {
        cumulatedFilters[appliedFilterType] = `${cumulatedFilters[appliedFilterType]}|${appliedFilterValue}`;
      } else {
        cumulatedFilters[appliedFilterType] = appliedFilterValue;
      }
      return null;
    });
    dataLayerHandleEvent({
      event: 'filter_item',
      event_category: 'filters',
      event_detail_1: '',
      event_detail_2: '',
      event_detail_3: 'remove',
      cumulated_filters: cumulatedFilters,
    });
    handleApplyFilters(currentFilters, currentSort);
  };

  const handleReset = () => {
    dataLayerHandleEvent({
      event: 'filter_item',
      event_category: 'filters',
      event_detail_1: '',
      event_detail_2: '',
      event_detail_3: 'remove',
      cumulated_filters: {},
    });
    handleApplyFilters([], currentSort);
  };

  const filterGenderApplied = appliedFilters.filter((filterApplied) => filterApplied.includes('en_gender_uFilter'))?.length;
  const currentFiltersCount = filterGenderApplied ? appliedFilters.length - 1 : appliedFilters.length;

  return (
    <Layout
      {...defaultProps}
      breadcrumbs={false}
      scrollArrow={products && products.length > 0}
      extratags={[
        <link key="canonical" rel="canonical" href={`${process.env.INTEGRA_DOMAIN}/${locale}/search`} />,
        <meta key="robots" name="robots" content="noindex, nofollow" />,
      ]}
      pageName={'Search'}
    >
      <HeadSEO description="Search products" />
      <div>
        {emptyResults && (
          <Row>
            <Col span={24} className={styles.productListTitle}>
              <h1 className={styles.searchTitle}>{listName}</h1>
            </Col>
            <Col span={24}>
              <p
                className={styles.emptyResults}
                dangerouslySetInnerHTML={{
                  __html: t('searcher', 'no.results').replace(/(?:\r\n|\r|\n)/g, '<br />'),
                }}
              ></p>
            </Col>
          </Row>
        )}
        {!emptyResults && (
          <Row>
            <Col span={24} className={styles.productListTitle}>
              <h1 className={styles.searchTitle}>{listName}</h1>
            </Col>
            <Col span={24} className={styles.filterHeaderCol} id="plp-filter-nav">
              <FilterHeader
                open={filterPanelVisible}
                toggle={() => handleOnClickFilterBtn(!filterPanelVisible)}
                count={currentFiltersCount}
                filters={appliedFilters.reduce((acc, key) => {
                  const obj = { key, label: key.split(':').pop() };
                  if (acc[key]) acc[key].push(obj);
                  else acc[key] = [obj];
                  return acc;
                }, {})}
                onRemoveFilter={handleRemoveFilter}
                onReset={handleReset}
                total={currentTotal}
                onSort={handleClickSort}
                currentSort={currentSort}
                hiddenFilters={['Unisex', 'Kids-Unisex']}
              />
            </Col>
            <Col
              xs={{ span: filterPanelVisible ? 24 : 0 }}
              md={{ span: filterPanelVisible ? 8 : 0 }}
              lg={{ span: filterPanelVisible ? 8 : 0 }}
              xl={{ span: filterPanelVisible ? 8 : 0 }}
              xxl={{ span: filterPanelVisible ? 6 : 0 }}
              className={filterPanelVisible ? `${styles.filterPanelCol} ${styles.filterPanelVisible}` : styles.filterPanelCol}
              id="plp-filter-panel"
            >
              <FilterPanelSearcher
                appliedFilters={appliedFilters}
                availableFilters={availableFiltersKeys}
                facets={useCurrentFacets ? currentFacets : facets}
                currentSort={currentSort}
                onApply={handleApplyFilters}
                total={currentTotal}
                onClickCloseBtn={handleOnClickFilterBtn}
                baseAvailable={{}}
                filtersCount={appliedFilters.length}
                country={country}
                lang={isoLanguage}
                camperlab={false}
              />
            </Col>
            <Col
              className={styles.gridColWrapper}
              xs={{ span: 24 }}
              md={{ span: filterPanelVisible ? 16 : 24 }}
              lg={{ span: filterPanelVisible ? 16 : 24 }}
              xl={{ span: filterPanelVisible ? 16 : 24 }}
              xxl={{ span: filterPanelVisible ? 18 : 24 }}
            >
              <Row className={styles.rowGrid}>
                {loading ?
                  Array(DEFAULT_LIMIT)
                    .fill()
                    .map((n, i) => (
                      <Col
                        xs={{ span: 12 }}
                        md={{ span: filterPanelVisible ? 12 : 8 }}
                        lg={{ span: filterPanelVisible ? 8 : 6 }}
                        xl={{ span: filterPanelVisible ? 8 : 6 }}
                        xxl={{ span: filterPanelVisible ? 8 : 6 }}
                        key={`${i}f`}
                      >
                        <ProductPlaceholder />
                      </Col>
                    ))
                : Object.keys(productsPerPage).map((pageKey) => {
                    const page = Number(pageKey);
                    return (
                      <PaginatedGrid
                        key={pageKey}
                        page={page}
                        products={productsPerPage[page].map((product) => {
                          const productWithTarget = {
                            ...product,
                            target: targetsTranslations[product.targetId],
                          };
                          return productWithTarget;
                        })}
                        currentList={'view_search_results'}
                        locale={locale}
                        colXS={{ span: 12 }}
                        colMD={{ span: filterPanelVisible ? 12 : 8 }}
                        colLG={{ span: filterPanelVisible ? 8 : 6 }}
                        colXL={{ span: filterPanelVisible ? 8 : 6 }}
                        colXXL={{ span: filterPanelVisible ? 8 : 6 }}
                        handleLoadNextPage={handleLoadNextPage}
                        withHover={false}
                        recalculating={false}
                        voucherApplied={false}
                        withLabel
                        currentListName={listName}
                      />
                    );
                  })
                }
              </Row>
            </Col>
          </Row>
        )}
      </div>
    </Layout>
  );
}

Search.propTypes = {
  products: array,
  searchTerm: string,
  locale: string,
  footerProps: any,
  tnmProps: any,
  tierProps: any,
  ribbon: any,
  urls: array,
  totalProducts: number,
  facets: any,
  filteredFacets: any,
  unbxdRequestId: string,
  unbxdTranslations: array,
  fallback: any,
};

Search.i18nPage = 'Search';

export async function getServerSideProps(context) {
  const { locale, query, req } = context;
  const ipAddress = req.socket.remoteAddress || req.headers['x-real-ip'];
  const market = getMarket(locale);
  const lang = getLang(locale);
  const isoLanguage = getRealLanguage(lang);
  const querySearch = getIfBlank(query.searchTerm, '');
  const queryMode = getIfBlank(query.mode, '');
  const imageID = query.imageID;
  const unbxdUid = req.cookies[COOKIES.UNBXD_UID] || null;

  const [{ defaultProps }, profileData, unbxdApiKey, unbxdSiteKey] = await Promise.all([
    initCMS({
      context,
      locale,
    }),
    checkProfile({ context, profile: locale }),
    getSingleTranslation(locale, 'buscador', 'unbxd.api.key'),
    getSingleTranslation(locale, 'buscador', 'unbxd.site.key'),
  ]);
  const unbxdTranslations = [unbxdApiKey, unbxdSiteKey];
  const emptyApiKeys = unbxdApiKey === '' || unbxdSiteKey === '';
  const unbxdActive = !emptyApiKeys;

  try {
    if (queryMode === 'findSimilar' && imageID) {
      const imageUrl = await retrieveImageForSearch({ profile: locale, id: imageID, context });

      if (imageUrl) {
        const byImageResult = await searchByImageUnbxd({
          lang: isoLanguage,
          market,
          image: imageUrl,
          translations: unbxdTranslations,
          uid: unbxdUid,
        });

        const { products, facets, numberOfProducts, unbxdRequestId, fallback } = byImageResult;

        const responseProductsFormatted = getProductsUnbxdFormatted(products, locale) ?? [];
        const totalProducts = numberOfProducts || 0;
        const filteredFacets = facets || {};

        if (totalProducts === 1 && responseProductsFormatted[0].url) {
          const redirectUrl = responseProductsFormatted[0].url;
          return {
            redirect: {
              permanent: false,
              destination: redirectUrl,
            },
          };
        }

        return {
          props: {
            defaultProps,
            searchTerm: '',
            products: responseProductsFormatted || [],
            fallback: fallback ?? null,
            totalProducts,
            facets,
            filteredFacets,
            unbxdRequestId,
            unbxdTranslations,
            queryMode,
            imageUrl,
          },
        };
      }
    }
  } catch (e) {
    console.error(`[Image Search Error] - Image ID: ${imageID}`, e);
  }

  if (unbxdActive && querySearch === '') {
    return {
      redirect: {
        destination: `/${locale}/search?searchTerm=${getDomainValue({ camper: 'Camper', camperlab: 'Camperlab', nnormal: 'Nnormal' })}`,
        permanent: false,
      },
    };
  }
  // Esto se debe a que tenemos un rewrite que fue atacado /buscador?qs=... y no queremos que se ejecute la busqueda
  if (query.skipSearch === 'true') {
    return {
      props: {
        defaultProps,
        searchTerm: querySearch,
        products: [],
        fallback: { originial: 'shoes', query: 'shoes' },
        urls: [],
        totalProducts: 0,
        facets: {},
        filteredFacets: {},
        unbxdRequestId: [],
        unbxdTranslations,
        queryMode,
      },
    };
  }

  let totalProducts = 0;
  let facets = {};
  let filteredFacets = {};
  let unbxdRequestId = '';

  if (querySearch && querySearch.length >= minSearchLength) {
    const filter = '';
    const responseWithoutFilters = {};

    const response = await searchProductsUnbxd({
      query: querySearch,
      lang: isoLanguage,
      filter,
      market,
      uid: unbxdUid,
      ipAddress,
      translations: unbxdTranslations,
      page: '1',
    });

    if (response?.redirect?.type === 'url' && !isBlank(response?.redirect?.value)) {
      return {
        redirect: {
          destination: response?.redirect?.value,
          permanent: false,
        },
      };
    }

    if (response === false) {
      return {
        props: {
          defaultProps,
          searchTerm: querySearch,
          products: [],
          facets,
          filteredFacets,
          unbxdRequestId,
          unbxdTranslations,
          queryMode,
        },
      };
    }

    const { products: responseProducts, fallback } = response;

    let responseProductsFormatted = responseProducts;

    responseProductsFormatted = getProductsUnbxdFormatted(responseProducts, locale);
    totalProducts = response?.numberOfProducts || 0;
    if (totalProducts === 1 && responseProductsFormatted[0].url) {
      const redirectUrl = responseProductsFormatted[0].url;
      return {
        redirect: {
          permanent: false,
          destination: redirectUrl,
        },
      };
    }
    facets = filter && filter.length ? responseWithoutFilters?.facets || {} : response?.facets || {};
    filteredFacets = response?.facets || {};
    unbxdRequestId = response?.unbxdRequestId;

    return {
      props: {
        defaultProps,
        searchTerm: querySearch,
        products: responseProductsFormatted || [],
        fallback: fallback || null,
        totalProducts,
        facets,
        filteredFacets,
        unbxdRequestId,
        unbxdTranslations,
        queryMode,
      },
    };
  }

  // fallback pero no deberiamos llegar a este return
  return {
    props: {
      defaultProps,
      searchTerm: querySearch,
      totalProducts,
      facets,
      filteredFacets,
      unbxdRequestId,
      unbxdTranslations,
      queryMode,
    },
  };
}

export default Search;
